import { useState, useEffect, useCallback } from "react";
import { workspaceApi, chatbotApi, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, MessageCircle, ChevronRight, Download } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, AreaChart, Area } from "recharts";
import { format, subDays } from "date-fns";
import { toast } from "sonner";

const DATE_PRESETS = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
];

/* Only state real numbers. A figure we could not load stays a dash. */
const num = (v) => (v === null || v === undefined || Number.isNaN(Number(v)) ? "—" : Number(v).toLocaleString());
const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

/* A quiet section inside the advanced disclosure: heading, one line of
   context, then whatever the section already rendered. */
function Block({ title, description, children, testId }) {
  return (
    <section data-testid={testId}>
      <h3 className="text-[16px] font-bold tracking-[-0.03em] text-b2-ink">{title}</h3>
      {description ? <p className="mt-1 text-[13px] text-b2-muted">{description}</p> : null}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Figure({ label, value, helper }) {
  return (
    <div className="rounded-[10px] border border-b2-line px-4 py-3">
      <div className="text-[13px] text-b2-muted">{label}</div>
      <div className="mt-1 text-[20px] font-bold tabular-nums tracking-[-0.02em] text-b2-ink">{value}</div>
      {helper ? <div className="mt-0.5 text-[12px] text-b2-faint">{helper}</div> : null}
    </div>
  );
}

export default function Analytics() {
  const [analytics, setAnalytics] = useState(null);
  const [ratings, setRatings] = useState(null);
  const [hourly, setHourly] = useState(null);
  const [leadsByBot, setLeadsByBot] = useState({ items: [], total: 0 });
  const [avgLen, setAvgLen] = useState(null);
  const [chatbots, setChatbots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hourlyMessages, setHourlyMessages] = useState(null);
  const [cacheSummary, setCacheSummary] = useState(null);
  const [failingIntents, setFailingIntents] = useState(null);
  const [lengthDist, setLengthDist] = useState(null);
  const [cacheRate, setCacheRate] = useState(null);
  const [cacheTrend, setCacheTrend] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [utm, setUtm] = useState(null);
  const [botPerf, setBotPerf] = useState(null);
  const [msgHourly, setMsgHourly] = useState([]);
  const [filterBot, setFilterBot] = useState("all");
  const [activePreset, setActivePreset] = useState(30);
  const [dateRange, setDateRange] = useState({ from: subDays(new Date(), 30), to: new Date() });
  const [rangeOpen, setRangeOpen] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [filterBot, dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { from: dateRange.from.toISOString(), to: dateRange.to.toISOString() };
      if (filterBot !== "all") params.chatbot_id = filterBot;
      const [analyticsRes, botsRes, ratingsRes, hourlyRes, leadsByBotRes, avgLenRes] = await Promise.all([workspaceApi.getAnalytics(params), chatbotApi.list(), workspaceApi.ratingsSummary(30).catch(() => ({ data: null })), workspaceApi.ratingsHourly(30).catch(() => ({ data: null })), workspaceApi.leadsByChatbot(30).catch(() => ({ data: { items: [], total: 0 } })), workspaceApi.convAvgLength(30).catch(() => ({ data: null }))]);
      // Batch 19: hourly volume + cache summary
      try {
        const [hRes, cRes, fRes, dRes, rRes, trendRes, pagesRes, msgHourlyRes, utmRes, perfRes] = await Promise.all([
          workspaceApi.messagesByHour(7).catch(() => ({ data: null })),
          workspaceApi.cacheSummary().catch(() => ({ data: null })),
          workspaceApi.failingIntents(7, 10).catch(() => ({ data: null })),
          workspaceApi.lengthDistribution(30).catch(() => ({ data: null })),
          workspaceApi.cacheRate(7).catch(() => ({ data: null })),
        workspaceApi.cacheRateTrend(14).catch(() => ({ data: { points: [] } })),
        workspaceApi.topPages(7, 10).catch(() => ({ data: { pages: [] } })),
        workspaceApi.utmBreakdown(30).catch(() => ({ data: null })),
        workspaceApi.messagesHourly(7).catch(() => ({ data: { buckets: [] } })),
        workspaceApi.botPerformance(30).catch(() => ({ data: { bots: [] } })),
        ]);
        setHourlyMessages(hRes?.data || null);
        setCacheSummary(cRes?.data || null);
        setFailingIntents(fRes?.data || null);
        setLengthDist(dRes?.data || null);
        setCacheRate(rRes?.data || null);
      setCacheTrend(trendRes?.data?.points || []);
      setTopPages(pagesRes?.data?.pages || []);
      setMsgHourly(msgHourlyRes?.data?.buckets || []);
    setUtm(utmRes?.data || null);
    setBotPerf(perfRes?.data || null);
      } catch (e) { /* silent */ }
      setAnalytics(analyticsRes.data);
      setRatings(ratingsRes?.data || null); setHourly(hourlyRes?.data || null);
      setLeadsByBot(leadsByBotRes?.data || { items: [], total: 0 }); setChatbots(botsRes.data); setAvgLen(avgLenRes?.data || null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const handlePreset = (days) => {
    setActivePreset(days);
    setDateRange({ from: subDays(new Date(), days), to: new Date() });
  };

  const handleExportCsv = async () => {
    try {
      const res = await workspaceApi.analyticsExportCsv(activePreset || 30);
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `bicarra-analytics-${activePreset || 30}d.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { /* ignore */ }
  };

  if (loading && !analytics) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-5 h-5 animate-spin text-b2-faint" /></div>;
  }

  const perChatbot = analytics?.per_chatbot || [];
  const accent = "#4963E8"; // the one primary colour
  const accent2 = "#94A3B8"; // quiet secondary line
  const hasChartData = (analytics?.chart_data?.length || 0) > 0;
  const rangeLabel = (DATE_PRESETS.find(p => p.days === activePreset) || {}).label || "Last 30 days";

  return (
    <div data-testid="analytics-page">
      {/* Eyebrow + title, with the date range kept quietly to the right. */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-[13px] text-b2-muted">Insights</p>
          <h1 className="mt-1 text-[26px] leading-tight font-bold tracking-[-0.02em] text-b2-ink">
            How your assistants are doing
          </h1>
        </div>
        <Select
          open={rangeOpen}
          onOpenChange={setRangeOpen}
          value={String(activePreset)}
          onValueChange={(v) => handlePreset(Number(v))}
        >
          <SelectTrigger
            data-testid="analytics-date-range"
            className="w-[172px] h-10 rounded-[10px] bg-b2-surface border border-b2-linestrong text-[14px] text-b2-ink shadow-none focus:ring-0"
          >
            <SelectValue placeholder={rangeLabel} />
          </SelectTrigger>
          <SelectContent className="rounded-[10px]">
            {DATE_PRESETS.map(p => (
              <SelectItem key={p.days} value={String(p.days)} className="text-[14px]">{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* BC-022: three stat cards, same as Home. */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="insights-stats">
        {[
          { label: "Conversations", value: num(analytics?.total_sessions) },
          { label: "Contacts collected", value: num(analytics?.total_leads) },
          { label: "Visitor rating", value: ratings && ratings.total > 0 ? `${ratings.satisfaction}%` : "—" },
        ].map((s) => (
          <div key={s.label} className="b2-card p-5">
            <p className="b2-label">{s.label}</p>
            <div className="mt-2 text-[28px] leading-none font-bold tabular-nums tracking-[-0.02em] text-b2-ink">
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* One panel: the chart when there is something to draw, otherwise the
          single empty state that covers the whole period. */}
      <div className="mt-6 b2-card p-6" data-testid="analytics-primary-panel">
        {hasChartData ? (
          <>
            <h2 className="text-[16px] font-bold tracking-[-0.03em] text-b2-ink">Conversations over time</h2>
            <p className="mt-1 text-[13px] text-b2-muted">Sessions and unique visitors, day by day.</p>
            <div className="h-72 mt-6">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics.chart_data}>
                  <defs>
                    <linearGradient id="sessFill" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor={accent} stopOpacity={0.22} /><stop offset="100%" stopColor={accent} stopOpacity={0} /></linearGradient>
                    <linearGradient id="visFill" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor={accent2} stopOpacity={0.16} /><stop offset="100%" stopColor={accent2} stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EDEFF3" vertical={false} />
                  <XAxis dataKey="date" stroke="#8A8F98" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => format(new Date(v), "MMM d")} />
                  <YAxis stroke="#8A8F98" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E6E8EC", borderRadius: 10, fontSize: 13, boxShadow: "none" }} />
                  <Area type="monotone" dataKey="sessions" stroke={accent} strokeWidth={2} fill="url(#sessFill)" />
                  <Area type="monotone" dataKey="visitors" stroke={accent2} strokeWidth={2} fill="url(#visFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <div className="py-12 text-center" data-testid="analytics-empty">
            <MessageCircle className="w-8 h-8 mx-auto text-b2-faint" strokeWidth={1.8} />
            <p className="mt-4 text-[17px] font-semibold text-b2-ink">No conversations recorded in this period</p>
            <p className="mt-1.5 text-[14px] text-b2-muted">Try a wider date range to see earlier activity.</p>
            <Button
              onClick={() => setRangeOpen(true)}
              data-testid="analytics-change-range"
              className="mt-6 h-11 px-5 rounded-[10px] bg-b2-indigo hover:bg-b2-indigohover text-white text-[14px] font-semibold shadow-none"
            >
              Change date range
            </Button>
          </div>
        )}
      </div>

      {/* A quiet row that opens the per-assistant numbers. */}
      <div className="mt-4 b2-card overflow-hidden">
        <button
          type="button"
          onClick={() => setBreakdownOpen(o => !o)}
          aria-expanded={breakdownOpen}
          data-testid="chatbot-breakdown-toggle"
          className="w-full flex items-center justify-between gap-4 px-6 py-[18px] text-left hover:bg-b2-wash transition-colors"
        >
          <span className="text-[15px] font-semibold text-b2-ink">Assistant breakdown</span>
          <ChevronRight
            className={`w-[18px] h-[18px] text-b2-muted transition-transform ${breakdownOpen ? "rotate-90" : ""}`}
            strokeWidth={1.8}
          />
        </button>
        {breakdownOpen && (
          <div className="border-t border-b2-line" data-testid="chatbot-breakdown">
            {perChatbot.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="text-[12px] text-b2-muted">
                      <th className="text-left px-6 py-3 font-medium">Assistant</th>
                      <th className="text-right px-6 py-3 font-medium">Visitors</th>
                      <th className="text-right px-6 py-3 font-medium">Conversations</th>
                      <th className="text-right px-6 py-3 font-medium">Messages</th>
                      <th className="text-right px-6 py-3 font-medium">Avg msgs / conversation</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-b2-line">
                    {perChatbot.map(bot => (
                      <tr key={bot.chatbot_id}>
                        <td className="px-6 py-3.5 font-medium text-b2-ink">{bot.chatbot_name}</td>
                        <td className="px-6 py-3.5 text-right tabular-nums text-b2-inksoft">{num(bot.visitors)}</td>
                        <td className="px-6 py-3.5 text-right tabular-nums text-b2-inksoft">{num(bot.sessions)}</td>
                        <td className="px-6 py-3.5 text-right tabular-nums text-b2-inksoft">{num(bot.messages)}</td>
                        <td className="px-6 py-3.5 text-right tabular-nums text-b2-inksoft">{num(bot.avg_messages)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="px-6 py-5 text-[14px] text-b2-muted">No per-assistant activity in this period.</p>
            )}
          </div>
        )}
      </div>

      {/* Everything technical lives here, closed by default. */}
      <div className="mt-4 b2-card overflow-hidden">
        <button
          type="button"
          onClick={() => setAdvancedOpen(o => !o)}
          aria-expanded={advancedOpen}
          data-testid="advanced-reports-toggle"
          className="w-full flex items-center justify-between gap-4 px-6 py-[18px] text-left hover:bg-b2-wash transition-colors"
        >
          <span className="text-[15px] font-semibold text-b2-ink">Advanced reports</span>
          <ChevronRight
            className={`w-[18px] h-[18px] text-b2-muted transition-transform ${advancedOpen ? "rotate-90" : ""}`}
            strokeWidth={1.8}
          />
        </button>

        {advancedOpen && (
          <div className="border-t border-b2-line px-6 py-7 space-y-9" data-testid="advanced-reports">

            {/* Filter + export */}
            <Block title="Filter and export" description="Narrow every figure on this page to one assistant, or take the numbers with you.">
              <div className="flex flex-wrap items-center gap-3">
                <Select value={filterBot} onValueChange={setFilterBot}>
                  <SelectTrigger className="w-52 h-10 rounded-[10px] bg-b2-surface border border-b2-linestrong text-[14px] text-b2-ink shadow-none" data-testid="analytics-bot-filter">
                    <SelectValue placeholder="All assistants" />
                  </SelectTrigger>
                  <SelectContent className="rounded-[10px]">
                    <SelectItem value="all" className="text-[14px]">All assistants</SelectItem>
                    {chatbots.map(b => <SelectItem key={b.id} value={b.id} className="text-[14px]">{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={handleExportCsv}
                  data-testid="analytics-export-csv"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-[10px] border border-b2-linestrong bg-b2-surface text-[14px] font-medium text-b2-ink hover:bg-b2-wash transition-colors"
                >
                  <Download className="w-4 h-4" strokeWidth={1.8} /> Export CSV
                </button>
              </div>
            </Block>

            {/* Remaining headline metrics */}
            <Block title="Engagement detail" description="The rest of the numbers behind the three figures above.">
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                <Figure label="Unique visitors" value={num(analytics?.unique_visitors)} helper="in selected period" />
                <Figure label="Messages" value={num(analytics?.total_messages)} helper="sent & received" />
                <Figure
                  label="Avg messages / conversation"
                  value={analytics?.avg_messages_per_session === null || analytics?.avg_messages_per_session === undefined
                    ? "—"
                    : (analytics.avg_messages_per_session.toFixed?.(1) ?? analytics.avg_messages_per_session)}
                  helper="engagement depth"
                />
                <Figure label="Contact rate" value={pct(analytics?.lead_conversion_rate)} helper="of conversations" />
                <Figure
                  label="Cache hit rate"
                  value={pct(cacheRate?.cache_rate_pct)}
                  helper={cacheRate ? `${num(cacheRate.cache_hits)} of ${num(cacheRate.total)} (7d)` : "no data yet"}
                />
              </div>
            </Block>

            {/* Ratings */}
            <Block title="Customer satisfaction" description="Visitor ratings on AI responses across all assistants (last 30 days).">
              {ratings && ratings.total > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Figure label="Satisfaction" value={`${ratings.satisfaction}%`} helper={`${ratings.total} total ratings`} />
                  <Figure label="Helpful" value={num(ratings.up)} />
                  <Figure label="Not helpful" value={num(ratings.down)} />
                </div>
              ) : (
                <p className="text-[14px] text-b2-muted">No ratings yet. Visitors can rate AI messages from the chat widget.</p>
              )}
              {Array.isArray(ratings?.by_chatbot) && ratings.by_chatbot.length > 0 && (
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-[14px]">
                    <thead>
                      <tr className="text-left text-[12px] text-b2-muted">
                        <th className="py-2 font-medium">Assistant</th>
                        <th className="text-right font-medium">Up</th>
                        <th className="text-right font-medium">Down</th>
                        <th className="text-right font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-b2-line">
                      {ratings.by_chatbot.map((b) => (
                        <tr key={b.chatbot_id}>
                          <td className="py-2 text-b2-ink">{b.name || b.chatbot_id.slice(0, 8)}</td>
                          <td className="py-2 text-right tabular-nums text-b2-live">{b.up}</td>
                          <td className="py-2 text-right tabular-nums text-b2-attention">{b.down}</td>
                          <td className="py-2 text-right tabular-nums text-b2-inksoft">{b.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Block>

            {/* Ratings by hour */}
            {hourly && hourly.total > 0 && (
              <Block title="Ratings by hour of day" description="When visitors are most likely to rate AI responses (last 30 days)." testId="ratings-hourly">
                <div className="flex items-end gap-1" style={{ height: 96 }}>
                  {hourly.buckets.map((b) => {
                    const tot = (b.up || 0) + (b.down || 0);
                    const max = Math.max(1, ...hourly.buckets.map((x) => (x.up || 0) + (x.down || 0)));
                    const h = Math.round((tot / max) * 84);
                    const upPct = tot > 0 ? Math.round(((b.up || 0) / tot) * 100) : 0;
                    return (
                      <div key={b.hour} className="flex-1 flex flex-col items-center justify-end" title={`${b.hour}:00 — ${b.up || 0} up / ${b.down || 0} down`}>
                        <div className="w-full rounded-t-[3px]" style={{ height: `${h}px`, background: tot > 0 ? `linear-gradient(180deg, #16A34A 0%, #16A34A ${upPct}%, #D97706 ${upPct}%, #D97706 100%)` : "#EDEFF3" }} />
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between text-[12px] text-b2-faint mt-2">
                  <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span>
                </div>
              </Block>
            )}

            {/* Messages per day */}
            <Block title="Messages per day" description="Volume of messages handled by your assistants.">
              <div className="h-64">
                {hasChartData ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics.chart_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEFF3" vertical={false} />
                      <XAxis dataKey="date" stroke="#8A8F98" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => format(new Date(v), "MMM d")} />
                      <YAxis stroke="#8A8F98" fontSize={12} tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E6E8EC", borderRadius: 10, fontSize: 13, boxShadow: "none" }} cursor={{ fill: "#F4F5F8" }} />
                      <Bar dataKey="messages" fill={accent} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-[13px] text-b2-muted">No messages data</div>
                )}
              </div>
            </Block>

            {/* Conversation length */}
            {avgLen && (
              <Block title="Conversation length" description={`Average messages per conversation over the last ${avgLen.days || 30} days.`} testId="avg-length">
                <div className="grid grid-cols-3 gap-3">
                  <Figure label="Conversations" value={num(avgLen.conversations)} />
                  <Figure label="Messages" value={num(avgLen.messages)} />
                  <Figure label="Avg / conversation" value={num(avgLen.avg)} />
                </div>
              </Block>
            )}

            {/* Leads by chatbot */}
            {leadsByBot && leadsByBot.items && leadsByBot.items.length > 0 && (
              <Block title="Contacts by assistant" description={`How many contacts each assistant has captured over the last 30 days. Total: ${leadsByBot.total || 0}.`} testId="leads-by-bot">
                <div className="space-y-2.5">
                  {leadsByBot.items.slice(0, 10).map(it => {
                    const share = leadsByBot.total ? Math.round((it.count / leadsByBot.total) * 100) : 0;
                    return (
                      <div key={it.chatbot_id || it.name} className="flex items-center gap-3">
                        <div className="w-40 text-[14px] text-b2-inksoft truncate">{it.name || "Untitled"}</div>
                        <div className="flex-1 h-1.5 bg-b2-wash rounded-full overflow-hidden"><div className="h-1.5 bg-b2-indigo rounded-full" style={{ width: share + "%" }} /></div>
                        <div className="w-16 text-right text-[14px] tabular-nums text-b2-ink">{it.count}</div>
                        <div className="w-12 text-right text-[13px] tabular-nums text-b2-muted">{share}%</div>
                      </div>
                    );
                  })}
                </div>
              </Block>
            )}

            {/* Messages by hour */}
            <Block title="Messages by hour of day" description="When visitors message your assistants (last 7 days)." testId="messages-hourly">
              {hourlyMessages?.hours ? (
                <div>
                  <div className="flex items-end gap-1" style={{ height: 96 }}>
                    {hourlyMessages.hours.map((b) => {
                      const max = Math.max(1, ...hourlyMessages.hours.map((x) => x.count || 0));
                      const h = Math.round(((b.count || 0) / max) * 84);
                      return (
                        <div key={b.hour} className="flex-1 flex flex-col items-center justify-end" title={`${b.hour}:00 — ${b.count} messages`}>
                          <div className="w-full rounded-t-[3px]" style={{ height: `${h}px`, background: b.count > 0 ? accent : "#EDEFF3" }} />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between text-[12px] text-b2-faint mt-2">
                    <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span>
                  </div>
                  <p className="text-[13px] text-b2-muted mt-3">
                    Total: <strong className="font-semibold text-b2-ink">{hourlyMessages.total || 0}</strong> messages · Peak hour: <strong className="font-semibold text-b2-ink">{hourlyMessages.peak_hour}:00</strong> ({hourlyMessages.peak_count} messages)
                  </p>
                </div>
              ) : (
                <p className="text-[13px] text-b2-muted">No hourly data yet.</p>
              )}
              {msgHourly.length > 0 && (
                <p className="text-[13px] text-b2-faint mt-2">{msgHourly.length} hourly buckets recorded in the last 7 days.</p>
              )}
            </Block>

            {/* Cache health */}
            <Block title="Cache health" description="Workspace-wide LLM response cache performance." testId="cache-health">
              {cacheSummary ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <Figure label="Entries" value={num(cacheSummary.total_entries)} />
                    <Figure label="Cache hits" value={num(cacheSummary.total_hits)} />
                    <Figure label="API calls saved" value={num(cacheSummary.estimated_api_calls_saved)} />
                    <Figure label="Saved (est)" value={cacheSummary.estimated_cost_saved_usd === null || cacheSummary.estimated_cost_saved_usd === undefined ? "—" : `$${cacheSummary.estimated_cost_saved_usd.toFixed(4)}`} />
                  </div>
                  {cacheSummary.by_chatbot?.length > 0 && (
                    <div>
                      <div className="text-[13px] font-semibold text-b2-ink mb-2">Per-assistant breakdown</div>
                      <div className="rounded-[10px] border border-b2-line divide-y divide-b2-line">
                        {cacheSummary.by_chatbot.map((b) => (
                          <div key={b.chatbot_id} className="px-4 py-3 flex items-center justify-between gap-4 text-[14px]">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-b2-ink truncate">{b.name}</div>
                              <div className="text-[13px] text-b2-muted">
                                {b.cache_enabled ? `Enabled · TTL ${b.cache_ttl_hours}h` : "Disabled"}
                              </div>
                            </div>
                            <div className="text-[13px] text-b2-muted flex gap-4 shrink-0">
                              <span>{b.entries} entries</span>
                              <span><strong className="font-semibold text-b2-ink">{b.hits}</strong> hits</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-[13px] text-b2-muted">No cache data yet.</p>
              )}
            </Block>

            {/* Failing intents */}
            <Block title="Top failing intents" description="Questions that triggered LLM errors most often (last 7 days)." testId="failing-intents">
              {failingIntents?.intents?.length > 0 ? (
                <div className="rounded-[10px] border border-b2-line divide-y divide-b2-line">
                  {failingIntents.intents.map((it, idx) => (
                    <div key={idx} className="px-4 py-3 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[14px] font-medium text-b2-ink truncate">{it.chatbot_name}</div>
                        <div className="text-[13px] text-b2-muted truncate">{it.context || "chat"}</div>
                        {it.last_error && <div className="text-[13px] text-b2-attention truncate mt-0.5">{it.last_error}</div>}
                      </div>
                      <div className="text-[14px] font-semibold tabular-nums text-b2-attention shrink-0">{it.count}×</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[13px] text-b2-muted">No errors logged in the last 7 days.</p>
              )}
            </Block>

            {/* Length distribution */}
            <Block
              title="Conversation length distribution"
              description={`Histogram of messages per conversation over the last ${lengthDist?.days || 30} days. Avg ${lengthDist?.avg_messages ?? "—"} msgs/conv across ${lengthDist?.total_conversations ?? "—"} conversations.`}
              testId="length-dist"
            >
              {lengthDist?.buckets?.length > 0 ? (
                <div className="flex items-end gap-2 h-32">
                  {(() => {
                    const maxV = Math.max(1, ...lengthDist.buckets.map(b => b.count));
                    return lengthDist.buckets.map((b, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div className="text-[12px] tabular-nums text-b2-inksoft">{b.count}</div>
                        <div className="w-full bg-b2-indigo rounded-t-[3px]" style={{ height: `${Math.max(2, (b.count / maxV) * 100)}%` }} />
                        <div className="text-[12px] text-b2-muted">{b.label}</div>
                      </div>
                    ));
                  })()}
                </div>
              ) : (
                <p className="text-[13px] text-b2-muted">Not enough conversation data yet.</p>
              )}
            </Block>

            {/* Cache trend */}
            {cacheTrend.length > 0 && (
              <Block title="Cache hit rate trend" description="Daily cache hit % over the last 14 days." testId="cache-rate-trend-card">
                <div className="flex items-end gap-1 h-24">
                  {cacheTrend.map((p, i) => {
                    const h = Math.max(2, Math.round((p.rate || 0) * 0.8));
                    return (
                      <div key={i} className="flex-1 bg-b2-indigo opacity-60 hover:opacity-100 transition-opacity rounded-t-[3px]" style={{ height: h + "px" }} title={`${p.date}: ${p.rate}% (${p.cached}/${p.total})`} />
                    );
                  })}
                </div>
                <div className="flex justify-between text-[12px] text-b2-faint mt-2">
                  <span>{cacheTrend[0]?.date}</span>
                  <span>{cacheTrend[cacheTrend.length - 1]?.date}</span>
                </div>
              </Block>
            )}

            {/* Top pages */}
            {topPages.length > 0 && (
              <Block title="Top referrer pages" description="Pages bringing the most chat sessions in the last 7 days." testId="top-pages-card">
                <ul className="rounded-[10px] border border-b2-line divide-y divide-b2-line">
                  {topPages.map((p, i) => (
                    <li key={i} className="flex items-center justify-between px-4 py-2.5 text-[14px]">
                      <span className="truncate flex-1 mr-3 text-b2-inksoft">{p.page}</span>
                      <span className="tabular-nums text-[13px] text-b2-muted shrink-0">{p.sessions} session{p.sessions === 1 ? "" : "s"}</span>
                    </li>
                  ))}
                </ul>
              </Block>
            )}

            {/* UTM */}
            {utm && utm.total > 0 && (
              <Block title="Marketing attribution" description={`UTM breakdown across ${utm.total} sessions in the last ${utm.days} days.`} testId="utm-card">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {[{ label: "Source", items: utm.sources }, { label: "Medium", items: utm.mediums }, { label: "Campaign", items: utm.campaigns }].map(g => (
                    <div key={g.label}>
                      <div className="text-[13px] font-semibold text-b2-ink mb-1.5">{g.label}</div>
                      {g.items.length === 0 ? (
                        <div className="text-[13px] text-b2-muted py-1.5">No data</div>
                      ) : (
                        <ul className="divide-y divide-b2-line">
                          {g.items.slice(0, 8).map((it, i) => (
                            <li key={i} className="flex items-center justify-between py-2 text-[13px]">
                              <span className="truncate flex-1 mr-2 text-b2-inksoft">{it.name || "(empty)"}</span>
                              <span className="tabular-nums text-b2-ink">{it.sessions}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </Block>
            )}

            {/* Bot performance */}
            {botPerf && botPerf.bots && botPerf.bots.length > 0 && (
              <Block title="Assistant performance" description={`Per-assistant activity in the last ${botPerf.days} days.`} testId="bot-perf-card">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-[14px]">
                    <thead>
                      <tr className="text-left text-[12px] text-b2-muted border-b border-b2-line">
                        <th className="py-2 pr-4 font-medium">Assistant</th>
                        <th className="py-2 pr-4 font-medium text-right">Messages</th>
                        <th className="py-2 pr-4 font-medium text-right">Conversations</th>
                        <th className="py-2 pr-4 font-medium text-right">Contacts</th>
                        <th className="py-2 pr-0 font-medium text-right">Tokens</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-b2-line">
                      {botPerf.bots.map((b) => (
                        <tr key={b.bot_id}>
                          <td className="py-2.5 pr-4 truncate max-w-[14rem] text-b2-ink">{b.name}</td>
                          <td className="py-2.5 pr-4 text-right tabular-nums text-b2-inksoft">{b.messages}</td>
                          <td className="py-2.5 pr-4 text-right tabular-nums text-b2-inksoft">{b.conversations}</td>
                          <td className="py-2.5 pr-4 text-right tabular-nums text-b2-inksoft">{b.leads}</td>
                          <td className="py-2.5 pr-0 text-right tabular-nums text-b2-inksoft">{(b.tokens || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Block>
            )}

          </div>
        )}
      </div>
    </div>
  );
}
